import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Plus, Trash2, Image, Search, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bundleTypes = [
  { value: 'publication_bundle', label: 'Publication Bundle', description: 'Combine multiple publications' },
  { value: 'press_release_bundle', label: 'Press Release Package', description: 'Multi-site press release distribution' },
  { value: 'social_bundle', label: 'Social Media Bundle', description: 'Multiple social page posts' },
  { value: 'print_bundle', label: 'Print Media Bundle', description: 'Multiple print placements' },
  { value: 'cross_service', label: 'Cross Service Bundle', description: 'Mix of digital, print, and social' },
];

const AdminBundleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    bundle_type: 'publication_bundle',
    description: '',
    short_description: '',
    items: [],
    original_price: '',
    bundle_price: '',
    cost_price: '',
    vendor_id: '',
    deliverable_description: '',
    sample_report_url: '',
    guaranteed_links: '',
    turnaround_days_min: 3,
    turnaround_days_max: 7,
    image: '',
    features: [],
    categories: [],
    is_active: true,
    is_featured: false,
  });

  // Calculate margin preview
  const calculateMargin = () => {
    const selling = parseFloat(formData.bundle_price) || 0;
    const cost = parseFloat(formData.cost_price) || 0;
    if (selling === 0) return { margin: 0, percent: 0, partnerMargin: 0, isAtRisk: false };
    
    const margin = selling - cost;
    const percent = (margin / selling) * 100;
    const partnerMin = selling * 0.82;
    const partnerMargin = partnerMin - cost;
    
    return { margin, percent: percent.toFixed(1), partnerMargin, isAtRisk: partnerMargin < 0 };
  };

  // For item selection
  const [publications, setPublications] = useState([]);
  const [socialPages, setSocialPages] = useState([]);
  const [printMedia, setPrintMedia] = useState([]);
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [featureInput, setFeatureInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');

  useEffect(() => {
    fetchAvailableItems();
    fetchVendors();
    if (isEditing) {
      fetchBundle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchAvailableItems = async () => {
    try {
      const [pubRes, socialRes, printRes] = await Promise.all([
        axios.get(`${API}/publications`),
        axios.get(`${API}/social-pages`).catch(() => ({ data: [] })),
        axios.get(`${API}/print-media`).catch(() => ({ data: [] })),
      ]);
      setPublications(pubRes.data);
      setSocialPages(socialRes.data);
      setPrintMedia(printRes.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchBundle = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/bundles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const bundle = response.data.find(b => b.id === id);
      if (bundle) {
        setFormData({
          name: bundle.name || '',
          slug: bundle.slug || '',
          bundle_type: bundle.bundle_type || 'publication_bundle',
          description: bundle.description || '',
          short_description: bundle.short_description || '',
          items: bundle.items || [],
          original_price: bundle.original_price || '',
          bundle_price: bundle.bundle_price || '',
          cost_price: bundle.cost_price || '',
          vendor_id: bundle.vendor_id || '',
          deliverable_description: bundle.deliverable_description || '',
          sample_report_url: bundle.sample_report_url || '',
          guaranteed_links: bundle.guaranteed_links || '',
          turnaround_days_min: bundle.turnaround_days_min || 3,
          turnaround_days_max: bundle.turnaround_days_max || 7,
          image: bundle.image || '',
          features: bundle.features || [],
          categories: bundle.categories || [],
          is_active: bundle.is_active !== false,
          is_featured: bundle.is_featured || false,
        });
      }
    } catch (error) {
      toast.error('Failed to load bundle');
      navigate('/admin/bundles');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: !isEditing || !prev.slug ? generateSlug(name) : prev.slug
    }));
  };

  const addItem = (item, type) => {
    const newItem = {
      item_type: type,
      item_id: item.id,
      item_name: item.name,
      quantity: 1
    };
    
    // Check if already added
    if (formData.items.some(i => i.item_id === item.id)) {
      toast.error('Item already added');
      return;
    }

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setShowItemSelector(false);
  };

  const removeItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(i => i.item_id !== itemId)
    }));
  };

  const handleAddFeature = () => {
    if (featureInput.trim() && !formData.features.includes(featureInput.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== feature)
    }));
  };

  const handleAddCategory = () => {
    if (categoryInput.trim() && !formData.categories.includes(categoryInput.trim())) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, categoryInput.trim()]
      }));
      setCategoryInput('');
    }
  };

  const handleRemoveCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== cat)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-image`, uploadData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, image: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.bundle_price) {
      toast.error('Name and price are required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        ...formData,
        original_price: parseFloat(formData.original_price) || parseFloat(formData.bundle_price),
        bundle_price: parseFloat(formData.bundle_price),
        cost_price: parseFloat(formData.cost_price) || 0,
        vendor_id: formData.vendor_id || null,
        guaranteed_links: formData.guaranteed_links ? parseInt(formData.guaranteed_links) : null,
        turnaround_days_min: parseInt(formData.turnaround_days_min) || 3,
        turnaround_days_max: parseInt(formData.turnaround_days_max) || 7,
      };

      if (isEditing) {
        await axios.put(`${API}/admin/bundles/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bundle updated');
      } else {
        await axios.post(`${API}/admin/bundles`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bundle created');
      }
      
      navigate('/admin/bundles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Filter items based on search
  const filteredPublications = publications.filter(p => 
    p.name.toLowerCase().includes(itemSearchQuery.toLowerCase())
  );
  const filteredSocialPages = socialPages.filter(p => 
    p.name.toLowerCase().includes(itemSearchQuery.toLowerCase())
  );
  const filteredPrintMedia = printMedia.filter(p => 
    p.name.toLowerCase().includes(itemSearchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl" data-testid="admin-bundle-form">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/bundles')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">
            {isEditing ? 'Edit Bundle' : 'Create Bundle'}
          </h1>
          <p className="text-sm text-gray-500">
            {isEditing ? 'Update bundle details' : 'Create a new combo package or press release bundle'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Bundle Type Selection */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Bundle Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {bundleTypes.map(type => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, bundle_type: type.value }))}
                className={`p-4 border rounded-lg text-left transition-all ${
                  formData.bundle_type === type.value
                    ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-[#1a1a1a]">{type.label}</p>
                    <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                  </div>
                  {formData.bundle_type === type.value && (
                    <Check className="w-5 h-5 text-[#D4AF37]" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bundle Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleNameChange}
                placeholder="e.g., Startup PR Pack"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="startup-pr-pack"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
            <input
              type="text"
              name="short_description"
              value={formData.short_description}
              onChange={handleChange}
              placeholder="Brief one-liner about this bundle..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Detailed description of what's included..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
            />
          </div>

          {/* Image Upload */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Bundle Image</label>
            <div className="flex items-center gap-4">
              {formData.image ? (
                <img src={formData.image} alt="Bundle" className="w-24 h-16 rounded object-cover" />
              ) : (
                <div className="w-24 h-16 rounded bg-gray-100 flex items-center justify-center">
                  <Image className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <label className="px-4 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <span className="text-sm">Upload Image</span>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Pricing</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Original Price (₹)</label>
              <input
                type="number"
                name="original_price"
                value={formData.original_price}
                onChange={handleChange}
                placeholder="Sum of individual items"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bundle Price (₹) *</label>
              <input
                type="number"
                name="bundle_price"
                value={formData.bundle_price}
                onChange={handleChange}
                placeholder="Discounted price"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
              <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
                {formData.original_price && formData.bundle_price ? (
                  `${Math.round((1 - formData.bundle_price / formData.original_price) * 100)}% OFF`
                ) : '-'}
              </div>
            </div>
          </div>
        </div>

        {/* Cost & Vendor */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Cost & Vendor</h2>
          <p className="text-sm text-gray-500 mb-4">Track costs for margin analysis</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost Price (₹)</label>
              <input
                type="number"
                name="cost_price"
                value={formData.cost_price}
                onChange={handleChange}
                placeholder="What you pay for all items"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
              <p className="text-xs text-gray-400 mt-1">Sum of all vendor costs for this bundle</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Vendor</label>
              <select
                name="vendor_id"
                value={formData.vendor_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
              >
                <option value="">Select vendor (optional)</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
              <a href="/admin/vendors" className="text-xs text-[#D4AF37] hover:underline mt-1 inline-block">Manage vendors →</a>
            </div>
          </div>

          {/* Margin Preview */}
          {formData.bundle_price && formData.cost_price && (
            <div className={`mt-4 p-4 rounded-lg ${calculateMargin().isAtRisk ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
              <h3 className="text-sm font-medium mb-2">
                {calculateMargin().isAtRisk ? '⚠️ Margin Warning' : '✓ Margin Preview'}
              </h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Your Margin</p>
                  <p className={`font-bold ${calculateMargin().margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    ₹{calculateMargin().margin.toLocaleString()} ({calculateMargin().percent}%)
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Partner Price (18% off)</p>
                  <p className="font-medium">₹{(parseFloat(formData.bundle_price) * 0.82).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-600">Partner Margin</p>
                  <p className={`font-bold ${calculateMargin().partnerMargin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    ₹{calculateMargin().partnerMargin.toLocaleString()}
                  </p>
                </div>
              </div>
              {calculateMargin().isAtRisk && (
                <p className="mt-2 text-xs text-red-600">
                  Warning: Partner discount would result in a loss. Consider increasing the bundle price to at least ₹{Math.ceil(parseFloat(formData.cost_price) / 0.82).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* For Press Release */}
        {formData.bundle_type === 'press_release_bundle' && (
          <div className="bg-white border border-gray-100 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Press Release Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guaranteed Links</label>
                <input
                  type="number"
                  name="guaranteed_links"
                  value={formData.guaranteed_links}
                  onChange={handleChange}
                  placeholder="e.g., 100"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sample Report URL</label>
                <input
                  type="url"
                  name="sample_report_url"
                  value={formData.sample_report_url}
                  onChange={handleChange}
                  placeholder="Link to sample PDF report"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Deliverable Description</label>
              <input
                type="text"
                name="deliverable_description"
                value={formData.deliverable_description}
                onChange={handleChange}
                placeholder="e.g., PDF report with all live links within 5 days"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
          </div>
        )}

        {/* Bundle Items */}
        {formData.bundle_type !== 'press_release_bundle' && (
          <div className="bg-white border border-gray-100 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#1a1a1a]">Bundle Items</h2>
              <button
                type="button"
                onClick={() => setShowItemSelector(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            {formData.items.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">No items added yet. Click &quot;Add Item&quot; to include publications, social pages, or print media.</p>
            ) : (
              <div className="space-y-2">
                {formData.items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-[#1a1a1a]">{item.item_name}</p>
                      <p className="text-sm text-gray-500 capitalize">{item.item_type?.replace(/_/g, ' ')}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(item.item_id)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAT */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Turnaround Time</h2>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Days</label>
              <input
                type="number"
                name="turnaround_days_min"
                value={formData.turnaround_days_min}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Days</label>
              <input
                type="number"
                name="turnaround_days_max"
                value={formData.turnaround_days_max}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Features (Bullet Points)</h2>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={featureInput}
              onChange={(e) => setFeatureInput(e.target.value)}
              placeholder="Add a feature..."
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
            />
            <button
              type="button"
              onClick={handleAddFeature}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {formData.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="flex-1 text-sm">{feature}</span>
                <button type="button" onClick={() => handleRemoveFeature(feature)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Categories</h2>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={categoryInput}
              onChange={(e) => setCategoryInput(e.target.value)}
              placeholder="Add category..."
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
            />
            <button
              type="button"
              onClick={handleAddCategory}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.categories.map((cat, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                {cat}
                <button type="button" onClick={() => handleRemoveCategory(cat)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Settings</h2>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-[#D4AF37] rounded focus:ring-[#D4AF37]"
              />
              <span className="text-sm">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleChange}
                className="w-4 h-4 text-[#D4AF37] rounded focus:ring-[#D4AF37]"
              />
              <span className="text-sm">Featured on Homepage</span>
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/bundles')}
            className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960F] disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Save Changes' : 'Create Bundle'}
          </button>
        </div>
      </form>

      {/* Item Selector Modal */}
      {showItemSelector && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#1a1a1a]">Add Items to Bundle</h3>
              <button onClick={() => setShowItemSelector(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={itemSearchQuery}
                onChange={(e) => setItemSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg"
              />
            </div>

            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Publications */}
              {filteredPublications.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Publications</h4>
                  <div className="space-y-1">
                    {filteredPublications.slice(0, 5).map(pub => (
                      <button
                        key={pub.id}
                        type="button"
                        onClick={() => addItem(pub, 'publication')}
                        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                      >
                        <span>{pub.name}</span>
                        <Plus className="w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Social Pages */}
              {filteredSocialPages.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Social Pages</h4>
                  <div className="space-y-1">
                    {filteredSocialPages.slice(0, 5).map(page => (
                      <button
                        key={page.id}
                        type="button"
                        onClick={() => addItem(page, 'social_page')}
                        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                      >
                        <span>{page.name} <span className="text-gray-400 text-sm">({page.platform})</span></span>
                        <Plus className="w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Print Media */}
              {filteredPrintMedia.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Print Media</h4>
                  <div className="space-y-1">
                    {filteredPrintMedia.slice(0, 5).map(media => (
                      <button
                        key={media.id}
                        type="button"
                        onClick={() => addItem(media, 'print_placement')}
                        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                      >
                        <span>{media.name}</span>
                        <Plus className="w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBundleForm;
